import { create } from "zustand";
import { persist } from "zustand/middleware";

export type LoginResult =
  | { ok: true }
  | { ok: false; reason: "invalid_credentials" | "server_error" | "network_error" };

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      token: null,

      login: async (username: string, password: string): Promise<LoginResult> => {
        let response: Response;
        try {
          response = await fetch("/api/v1/admin/auth/login", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ username, password }),
          });
        } catch {
          // fetch only rejects on network-level failures (offline, DNS, CORS).
          return { ok: false, reason: "network_error" };
        }

        if (!response.ok) {
          // 401/403 → bad credentials; anything else (5xx, etc.) → server problem.
          if (response.status === 401 || response.status === 403) {
            return { ok: false, reason: "invalid_credentials" };
          }
          return { ok: false, reason: "server_error" };
        }

        try {
          const data = await response.json();
          set({ isAuthenticated: true, token: data.token });
          return { ok: true };
        } catch {
          return { ok: false, reason: "server_error" };
        }
      },

      logout: () => {
        set({ isAuthenticated: false, token: null });
      },
    }),
    {
      name: "admin-auth",
      partialize: (state) => ({ isAuthenticated: state.isAuthenticated, token: state.token }),
    }
  )
);
